import React, { useEffect, useState, useContext } from 'react';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import DateFnsUtils from '@date-io/moment';
import { CSVLink } from 'react-csv';

import { useTranslation, Trans } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import Tooltip from '@material-ui/core/Tooltip';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';

// icon
import GetAppIcon from '@material-ui/icons/GetApp';
import { DamIcon } from '../../../../assets/svg/Icon';
import { StyledFormButton } from '../../../../components/button/Button';

// Provider
import ModeContext from '../../../../context/ModeContext';
import UserContext from '../../../../context/UserContext';

// components
import DamModal from '../../../../components/modal/Modal';
import DamLegend from '../../legend/DamLegend';
import NavForm from '../../formcontrol/FormControl';
import DamMediumDataLists from './DamMediumDataLists';
import areas from '../../../../assets/json/region';
import Loading from '../../../../hocs/loading';

// style
import '../../styles.css';
import { globalStyle } from '../../../../global/style';

const useStyles = makeStyles(globalStyle);

// export config
const headers = [
  { label: <Trans>อ่างเก็บน้ำ</Trans>, key: 'dam_name' },
  { label: <Trans>จังหวัด</Trans>, key: 'province_name' },
  { label: <Trans>ลุ่มน้ำ</Trans>, key: 'basin_name' },
  { label: <Trans>กักเก็บปกติ (ล้าน ลบ.ม.)</Trans>, key: 'normal_storage' },
  { label: <Trans>กักเก็บต่ำสุด (ล้าน ลบ.ม.)</Trans>, key: 'min_storage' },
  { label: <Trans>กักเก็บ (ล้าน ลบ.ม.)</Trans>, key: 'dam_storage' },
  { label: <Trans>กักเก็บ (% ความจุ)</Trans>, key: 'dam_storage_percent' },
  { label: <Trans>ปริมาตรใช้การ (ล้าน ลบ.ม.)</Trans>, key: 'dam_uses_water' },
  { label: <Trans>น้ำไหลลง (ล้าน ลบ.ม.)</Trans>, key: 'dam_inflow' },
  { label: <Trans>น้ำระบาย (ล้าน ลบ.ม.)</Trans>, key: 'dam_released' },
];
const datas = [];

// set total summary
let grandTotalNormalStorage = 0;
let grandTotalMinStorage = 0;
let grandTotalInflow = 0;
let grandTotalReleased = 0;
let grandTotalDamStorage = 0;
let grandTotalDamUseWater = 0;

export default function Main() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [datasMediumDam, setMediumDamData] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [maxDate, setMaxDate] = useState(moment().format('YYYY-MM-DD'));
  const [csv, setCsv] = useState({
    headers: [],
    data: [],
  });

  // modal
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
    subheader: null,
    content: null,
  });

  // get context
  const { currentMode, currentArea } = useContext(ModeContext);
  const { userIsLogin } = useContext(UserContext);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = currentData.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = currentData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const fetchData = async (d) => {
    setLoading(true);

    let url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=2&dam_date=${d}`;
    if (d === undefined) {
      url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=2`;
    }

    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Something went wrong ...');
      })
      .then((result) => {
        /*
        Add filter by province or basin later
        */
        const datasDamMedium = orderBy(result.data.dam_medium, ['dam_storage_percent'], ['desc']);

        // custom render export data
        if (areas.length === 7) {
          // remove อื่นๆ
          areas.splice(-1, 1);
        }

        areas.map((area) => {
          datas.push({
            dam_name: area.name,
          });

          // summary value by region
          let totalNormalStorage = 0;
          let totalMinStorage = 0;
          let totalInflow = 0;
          let totalReleased = 0;
          let totalDamStorage = 0;
          let totalDamUseWater = 0;

          datasDamMedium
            .filter((row) => row.geocode.area_code === area.id.toString())
            .map((row) => {
              totalNormalStorage += row.dam.normal_storage;
              if (row.dam.min_storage) {
                totalMinStorage += row.dam.min_storage;
              }
              totalInflow += row.dam_inflow;
              totalReleased += row.dam_released;
              totalDamStorage += row.dam_storage;
              totalDamUseWater += row.dam_uses_water;

              // grand total
              grandTotalNormalStorage += row.dam.normal_storage;
              if (row.dam.min_storage) {
                grandTotalMinStorage += row.dam.min_storage;
              }
              grandTotalInflow += row.dam_inflow;
              grandTotalReleased += row.dam_released;
              grandTotalDamStorage += row.dam_storage;
              grandTotalDamUseWater += row.dam_uses_water;

              datas.push({
                dam_name: row.dam.dam_name.th,
                province_name: row.geocode.province_name.th,
                basin_name: row.basin.basin_name.th,
                normal_storage: row.dam.normal_storage,
                min_storage: row.dam.min_storage,
                dam_storage: row.dam_storage,
                dam_storage_percent: row.dam_storage_percent,
                dam_uses_water: row.dam_uses_water,
                dam_inflow: row.dam_inflow,
                dam_released: row.dam_released,
              });

              return null;
            });

          // push summary data for each region
          datas.push({
            dam_name: t(`รวม${area.name}`),
            normal_storage: totalNormalStorage,
            min_storage: totalMinStorage,
            dam_storage: totalDamStorage,
            dam_uses_water: totalDamUseWater,
            dam_inflow: totalInflow,
            dam_released: totalReleased,
          });

          return null;
        });

        // grand total
        datas.push({
          dam_name: t('รวมทั้งประเทศ'),
          normal_storage: grandTotalNormalStorage,
          min_storage: grandTotalMinStorage,
          dam_storage: grandTotalDamStorage,
          dam_uses_water: grandTotalDamUseWater,
          dam_inflow: grandTotalInflow,
          dam_released: grandTotalReleased,
        });

        // set data state
        setMediumDamData(datasDamMedium);

        setSelectedDate(result.data.dam_medium[0].dam_date);
        if (typeof d === 'undefined') {
          setMaxDate(result.data.dam_medium[0].dam_date);
        }

        setCsv({
          headers,
          data: datas,
        });
      })
      .catch((err) => setError(err))
      .finally(() => { setLoading(false); });
  };

  const handleModalChanage = (d) => {
    setModalData(d);
  };

  const handleDateChange = (d) => {
    const myDate = moment(d).format('YYYY-MM-DD');
    setSelectedDate(myDate);

    fetchData(myDate);

    handleModalChanage({
      open: false,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: null,
    });
  };

  // fetch data
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.root}>
      {loading && <Loading />}

      {error}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} md={4}>
          <Box display="flex" alignItems="center">
            <Box display="flex" className={classes.shapeCircle} alignItems="center" justifyContent="center">
              <DamIcon style={{ color: 'white' }} />
            </Box>
            <Box p={1}><Typography variant="h6" className={classes.titlePage}>{t('แหล่งน้ำขนาดกลาง')}</Typography></Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={8}>
          <NavForm />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={9}>
          <div style={{ paddingTop: '35px' }}>
            <Typography variant="h5" component="h5">
              {`${t('ข้อมูลวันที่')} ${selectedDate ? moment(selectedDate).locale('th').format('DD MMM YYYY') : ''}`}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={8} sm={4} md={2}>
          {/* Search and Download */}
          <FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                margin="normal"
                variant="inline"
                format="YYYY-MM-DD"
                id="date-picker"
                label={t('ข้อมูลวันที่')}
                value={selectedDate || moment().subtract(1, 'days').format('YYYY-MM-DD')}
                maxDate={maxDate}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </MuiPickersUtilsProvider>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={2} md={1}>
          {userIsLogin && (
            <div style={{ paddingTop: '35px' }}>
              <CSVLink id="download_csv" data={csv.data} headers={csv.headers} filename={`dam_medium_${selectedDate}.csv`}>
                <Tooltip title={t('ดาวน์โหลดไฟล์ อ่างเก็บน้ำ')} placement="top">
                  <StyledFormButton
                    variant="outlined"
                    size="small"
                    className={classes.button}
                    startIcon={<GetAppIcon />}
                  >
                    {t('ดาวน์โหลด')}
                  </StyledFormButton>
                </Tooltip>
              </CSVLink>
            </div>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Box>
            <DamMediumDataLists
              datas={filterData(datasMediumDam, currentMode, currentArea)}
              latestDate={selectedDate}
              handleModalChanage={handleModalChanage}
            />
          </Box>
          <Box mt={2}>
            <DamLegend />
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={12} align="right">
          {t('ข้อมูลจากกรมชลประทาน')}
        </Grid>
      </Grid>

      <DamModal
        open={modalData.open}
        modalHeader={modalData.header}
        modalSubHeader={modalData.subheader}
        modalContent={modalData.content}
      />
    </div>
  );
}
